import React, { useState } from 'react'
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardMedia,
  useTheme,
  Button,
  Tabs,
  Tab
} from '@mui/material'
import JobCarousel from './JobCarousel'
import { useHandleChangeRoute } from 'data/hooks'
import RelocationVideo from 'static/videos/relocation.mp4'
import NoSpamVideo from 'static/videos/no_spam.mp4'
import RecruiterContactVideo from 'static/videos/recruiter_contact.mp4'
import AdvancePurposeVideo from 'static/videos/advance_purpose.mp4'

const HowItWorks = () => {
  const theme = useTheme()
  const [activeTab, setActiveTab] = useState(0)
  const handleChangeRoute = useHandleChangeRoute()

  const steps = [
    {
      id: 'no-spam',
      title: 'No Spam. No Games. Just Jobs.',
      headline: 'No Spam. No Games. Just Jobs.',
      description: 'You won\'t find popups or recruiter spam here—just clean, curated listings made for providers.',
      buttonText: 'Get Started',
      media: {
        type: 'video',
        src: NoSpamVideo,
        alt: 'Clean job listings demonstration'
      }
    },
    {
      id: 'straight-to-source', 
      title: 'Straight to the Source',
      headline: 'Straight to the Source',
      description: 'Every listing includes direct hospital contact info—so you can connect with decision-makers.',
      buttonText: 'Get Started',
      media: {
        type: 'video',
        src: RecruiterContactVideo,
        alt: 'Direct contact information demonstration'
      }
    },
    {
      id: 'advance-with-purpose',
      title: 'Advance with Purpose',
      headline: 'Advance with Purpose',
      description: 'Whether you\'re post-residency or mid-career, MyStethi helps you find a role that moves you forward.',
      buttonText: 'Get Started',
      media: {
        type: 'video',
        src: AdvancePurposeVideo,
        alt: 'Career advancement demonstration'
      }
    },
    {
      id: 'relocation-support',
      title: 'Integrated Relocation Support',
      headline: 'Integrated Relocation Support',
      description: 'Through our housing partner, we help you find a place to live and simplify your move, whether you\'re relocating across the state or across the country.',
      buttonText: 'Get Started',
      media: {
        type: 'video',
        src: RelocationVideo,
        alt: 'Relocation and housing support demonstration'
      }
    }
  ]

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  return (
    <>
      <Box sx={{ p: 4, backgroundColor: 'white' }}>
      <Container maxWidth="lg">

        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2
            }}
          >
            Healthcare Job Search Made Simple for Medical Professionals
          </Typography>
        </Box>

        {/* Tab Navigation */}
        <Box sx={{ mb: 6 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                display: 'none' // Remove underline from active tab
              },
              '& .MuiTab-root': {
                fontSize: { xs: '0.85rem', sm: '1rem' },
                fontWeight: 500,
                color: theme.palette.grey[900],
                textTransform: 'none',
                minWidth: { xs: 'auto', sm: 'auto' },
                px: { xs: 1.5, sm: 3 },
                py: { xs: 1, sm: 1.5 },
                '&.Mui-selected': {
                  color: theme.palette.primary.dark,
                  fontWeight: 600
                }
              },
              '& .MuiTabs-scrollButtons': {
                color: theme.palette.primary.dark,
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              }
            }}
          >
            {steps.map((step) => (
              <Tab key={step.id} label={step.title} />
            ))}
          </Tabs>
        </Box>

        {/* Content Area */}
        <Grid container spacing={6} alignItems="center">
          {/* Left Side - Text Content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ pr: { md: 4 } }}>
              <Typography
                variant="overline"
                sx={{
                  color: theme.palette.primary.dark,
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  mb: 1,
                  display: 'block',
                  fontSize: '0.9rem'
                }}
              >
                FOR CLINICIANS
              </Typography>
              
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  fontWeight: 700,
                  color: theme.palette.grey[900],
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                {steps[activeTab].headline}
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.grey[900],
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  mb: 4
                }}
              >
                {steps[activeTab].description}
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                onClick={() => handleChangeRoute('/register/physicians')()}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: theme.palette.shadows.colored.primary,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: theme.palette.shadows.colored.primaryHover
                  }
                }}
              >
                {steps[activeTab].buttonText}
              </Button>
            </Box>
          </Grid>

          {/* Right Side - Media Content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative' }}>
              <Card
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: theme.palette.shadows.medium,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.shadows.heavy
                  }
                }}
              >
                {steps[activeTab].media.type === 'video' ? (
                  <CardMedia
                    component="video"
                    src={steps[activeTab].media.src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    sx={{
                      width: '100%',
                      height: { xs: '300px', sm: '400px', md: '450px' },
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <CardMedia
                    component="img"
                    src={steps[activeTab].media.src}
                    alt={steps[activeTab].media.alt}
                    sx={{
                      width: '100%',
                      height: { xs: '300px', sm: '400px', md: '450px' },
                      objectFit: 'cover'
                    }}
                  />
                )}
              </Card>
            </Box>
          </Grid>
        </Grid>

      </Container>
      
      {/* Job Carousel - Full Width */}
      <JobCarousel />
      </Box>

    </>
  )
}

export default HowItWorks
